import React, { useEffect, useMemo, useState } from "react";
import {
	Alert,
	Button,
	Card,
	DatePicker,
	Descriptions,
	Progress,
	Select,
	Statistic,
	Tag,
	Tooltip,
} from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import dayjs, { Dayjs } from "dayjs";
import useLang from "../../../../hooks/useLang";
import {
	ClientOrderActivity,
	SellerOption,
	getClientOrderActivity,
	getSellerOptions,
	sellerLabel,
	sellerSearchText,
} from "../../../../api/client-reports";
import BulkOpsHeader from "../../components/BulkOpsHeader";
import { BRAND_PRIMARY, PRIMARY_BUTTON_STYLE } from "../../components/styles";
import {
	formatDate,
	formatDateTime,
	formatNumber,
	daysTagColor,
	toWireDate,
} from "../reportFormat";

const { RangePicker } = DatePicker;

const ClientActivity = () => {
	const { strings } = useLang("clientReports");
	const common = strings?.common;
	const copy = strings?.clientActivity;
	const reportStrings = strings?.reports?.clientActivity;

	const [sellerId, setSellerId] = useState<number | null>(null);
	const [sellers, setSellers] = useState<SellerOption[]>([]);
	const [sellersLoading, setSellersLoading] = useState(true);
	// Empty by default: the API then measures from the client's own registration date, which is
	// the "whole history" reading most people want first.
	const [range, setRange] = useState<[Dayjs, Dayjs] | null>(null);
	const [loading, setLoading] = useState(false);
	const [activity, setActivity] = useState<ClientOrderActivity | null>(null);

	// Loaded once on mount. ~1 000 active clients, so the whole list is cheap to hold and the
	// type-ahead can filter locally — no request per keystroke.
	useEffect(() => {
		let cancelled = false;

		getSellerOptions()
			.then((options) => {
				if (!cancelled) {
					setSellers(options);
				}
			})
			.catch(() => {
				// The interceptor toasts it; an empty list still allows typing a raw id.
			})
			.finally(() => {
				if (!cancelled) {
					setSellersLoading(false);
				}
			});

		return () => {
			cancelled = true;
		};
	}, []);

	const sellerOptions = useMemo(
		() =>
			sellers.map((seller) => ({
				value: seller.id,
				label: sellerLabel(seller),
				search: sellerSearchText(seller),
			})),
		[sellers]
	);

	const generate = async () => {
		if (!sellerId || sellerId <= 0) {
			return;
		}

		setLoading(true);
		try {
			const result = await getClientOrderActivity({
				sellerId,
				fromDate: range?.[0] ? toWireDate(range[0]) : undefined,
				toDate: range?.[1] ? toWireDate(range[1]) : undefined,
			});
			setActivity(result);
		} catch {
			// The interceptor toasts the API's message, including "client not found".
			setActivity(null);
		} finally {
			setLoading(false);
		}
	};

	const activePercent =
		activity && activity.totalDays > 0
			? Math.round((activity.daysWithOrders / activity.totalDays) * 100)
			: 0;

	return (
		<div className="max-w-5xl mx-auto px-4 py-6 flex flex-col gap-6">
			<BulkOpsHeader
				title={reportStrings?.label ?? "Aktiviteti i një klienti"}
				subtitle={
					reportStrings?.description ??
					"Për një klient: ditët me porosi kundrejt ditëve pa porosi"
				}
				backTo="/bulk-order-operations/client-reports"
				backLabel={common?.back ?? "Të gjitha raportet"}
			/>

			<div className="rounded-lg border border-gray-200 p-4 flex flex-col gap-4">
				<div className="grid grid-cols-1 md:grid-cols-3 gap-4">
					<label className="flex flex-col gap-1">
						<span className="text-sm text-gray-600">
							{copy?.sellerLabel ?? "Klienti"}
						</span>
						<Select
							showSearch
							allowClear
							value={sellerId}
							loading={sellersLoading}
							onChange={(value) => setSellerId(value ?? null)}
							options={sellerOptions}
							// Match on name, username, client code AND the numeric id, so a
							// SellerId copied from another report still finds its client.
							filterOption={(input, option) =>
								(option?.search ?? "").includes(
									input.trim().toLowerCase()
								)
							}
							placeholder={
								copy?.sellerPlaceholder ??
								"Kërko sipas emrit, kodit ose ID-së"
							}
							notFoundContent={
								sellersLoading
									? copy?.sellersLoading ?? "Duke ngarkuar…"
									: copy?.noSeller ?? "Asnjë klient"
							}
						/>
						<span className="text-xs text-gray-400">
							{copy?.sellerHint ??
								"Shkruaj për të kërkuar mes klientëve aktivë"}
						</span>
					</label>

					<label className="flex flex-col gap-1 md:col-span-2">
						<span className="text-sm text-gray-600">
							{copy?.periodLabel ?? "Periudha (opsionale)"}
						</span>
						<RangePicker
							value={range}
							format="DD/MM/YYYY"
							className="w-full"
							disabledDate={(current) =>
								current && current.isAfter(dayjs(), "day")
							}
							onChange={(value) =>
								setRange(
									value?.[0] && value?.[1]
										? [value[0], value[1]]
										: null
								)
							}
						/>
						<span className="text-xs text-gray-400">
							{copy?.periodHint ??
								"Bosh = që nga dita e regjistrimit të klientit deri sot"}
						</span>
					</label>
				</div>

				<div>
					<Button
						type="primary"
						style={!sellerId ? undefined : PRIMARY_BUTTON_STYLE}
						loading={loading}
						disabled={!sellerId}
						onClick={generate}
					>
						{common?.generate ?? "Gjenero raportin"}
					</Button>
				</div>
			</div>

			{activity && (
				<>
					<Card size="small">
						<Descriptions
							size="small"
							column={{ xs: 1, sm: 2, md: 3 }}
							title={
								<span className="flex items-center gap-2">
									<span>
										{activity.nameAndSurname?.trim() ||
											`#${activity.sellerId}`}
									</span>
									{activity.uniqueSellerIdentifier && (
										<Tag color="purple">
											{activity.uniqueSellerIdentifier}
										</Tag>
									)}
								</span>
							}
						>
							<Descriptions.Item
								label={common?.colBusiness ?? "Biznesi"}
							>
								{activity.businessName || "—"}
							</Descriptions.Item>
							<Descriptions.Item
								label={common?.colPhone ?? "Telefoni"}
							>
								{activity.phone || "—"}
							</Descriptions.Item>
							<Descriptions.Item
								label={
									common?.colBusinessPhone ??
									"Telefoni i biznesit"
								}
							>
								{activity.businessPhone || "—"}
							</Descriptions.Item>
						</Descriptions>
					</Card>

					{/*
					 * Registration date and first-order date are shown side by side on purpose:
					 * the database records no activation timestamp, so neither one alone is "the
					 * day they became a client" and the business decides which it means.
					 */}
					<Card size="small">
						<Descriptions
							size="small"
							column={{ xs: 1, sm: 2, md: 4 }}
						>
							<Descriptions.Item
								label={
									<span className="flex items-center gap-1">
										{copy?.registeredAt ?? "Regjistruar më"}
										<Tooltip
											title={
												copy?.registeredAtHint ??
												"Data kur u krijua llogaria. Baza nuk ruan datë aprovimi, prandaj tregohet edhe porosia e parë."
											}
										>
											<InfoCircleOutlined className="text-gray-400" />
										</Tooltip>
									</span>
								}
							>
								{formatDate(activity.registeredAt)}
							</Descriptions.Item>
							<Descriptions.Item
								label={copy?.firstOrder ?? "Porosia e parë"}
							>
								{formatDate(activity.firstOrderDate)}
							</Descriptions.Item>
							<Descriptions.Item
								label={copy?.lastOrder ?? "Porosia e fundit"}
							>
								{formatDateTime(activity.lastOrderDate)}
							</Descriptions.Item>
							<Descriptions.Item
								label={
									copy?.currentStreak ?? "Ditë pa porosi tani"
								}
							>
								{typeof activity.currentInactiveStreakDays ===
								"number" ? (
									<Tag
										color={daysTagColor(
											activity.currentInactiveStreakDays
										)}
									>
										{formatNumber(
											activity.currentInactiveStreakDays
										)}
									</Tag>
								) : (
									<Tag>
										{copy?.neverOrdered ?? "Asnjëherë"}
									</Tag>
								)}
							</Descriptions.Item>
						</Descriptions>
					</Card>

					<Card
						size="small"
						title={`${
							copy?.periodTitle ?? "Periudha e analizuar"
						}: ${formatDate(activity.periodFrom)} — ${formatDate(
							activity.periodTo
						)}`}
					>
						<div className="grid grid-cols-2 md:grid-cols-4 gap-4">
							<Statistic
								title={copy?.totalDays ?? "Ditë gjithsej"}
								value={activity.totalDays}
							/>
							<Statistic
								title={copy?.daysWith ?? "Ditë me porosi"}
								value={activity.daysWithOrders}
								valueStyle={{ color: "#389e0d" }}
							/>
							<Statistic
								title={copy?.daysWithout ?? "Ditë pa porosi"}
								value={activity.daysWithoutOrders}
								valueStyle={{ color: "#cf1322" }}
							/>
							<Statistic
								title={
									copy?.ordersInPeriod ?? "Porosi në periudhë"
								}
								value={activity.ordersInPeriod}
								valueStyle={{ color: BRAND_PRIMARY }}
							/>
						</div>

						<div className="mt-4">
							<Progress
								percent={activePercent}
								strokeColor={BRAND_PRIMARY}
								format={(percent) =>
									`${percent}% ${
										copy?.activeDays ?? "ditë aktive"
									}`
								}
							/>
						</div>
					</Card>

					<Card
						size="small"
						title={copy?.lifetimeTitle ?? "Gjithë historiku"}
					>
						<Alert
							className="mb-3"
							type="warning"
							showIcon
							message={
								copy?.lifetimeNote ??
								"Këto shifra janë për gjithë historikun e klientit, jo vetëm për periudhën e zgjedhur më lart."
							}
						/>
						<div className="flex flex-wrap items-center gap-4">
							<Statistic
								title={
									common?.colTotalOrders ??
									"Totali i porosive"
								}
								value={activity.totalOrders}
							/>
							<span className="flex flex-wrap gap-1">
								{activity.ordersKs > 0 && (
									<Tag color="blue">
										KS {formatNumber(activity.ordersKs)}
									</Tag>
								)}
								{activity.ordersAl > 0 && (
									<Tag color="geekblue">
										AL {formatNumber(activity.ordersAl)}
									</Tag>
								)}
								{activity.ordersMk > 0 && (
									<Tag color="purple">
										NMK {formatNumber(activity.ordersMk)}
									</Tag>
								)}
								{activity.ordersUnknownCountry > 0 && (
									<Tooltip
										title={
											common?.unknownCountryHint ??
											"Porosi që nuk lidhen me asnjë shtet."
										}
									>
										<Tag color="red">
											?{" "}
											{formatNumber(
												activity.ordersUnknownCountry
											)}
										</Tag>
									</Tooltip>
								)}
							</span>
						</div>
					</Card>
				</>
			)}

			{!activity && !loading && (
				<Alert
					type="info"
					showIcon
					message={
						copy?.notGenerated ??
						"Shkruaj ID-në e klientit dhe kliko Gjenero raportin"
					}
				/>
			)}
		</div>
	);
};

export default ClientActivity;
